import { forwardRef, Inject, Injectable } from "@angular/core";
import moment from "moment";
import { values } from "lodash";
import { TblBidDataDisplay } from "../models/BidDataDisplay";
import { List } from "linqts";
import { CalendarData } from "../models/CalendarData";
import { GlobalStore } from "./store-operations";
import {
  AppConstants,
  BidLineType,
  ColDefDataFormats,
  DaysofWeek,
} from "../enums/global-enum-constant";
import { CrewbidCollection } from "./crewbid-collection";
import { ItemType } from "../models/ItemType";
import { City, Lines } from "../models/Lines";
import { CrewbidState } from "../models/CrewbidState";
import { CalendarView } from "../models/ConstraintUIModel";
import * as momenttime from "moment-timezone";
import { Trip } from "../models/Trip";
import { uniq, orderBy } from "lodash";
import Swal from "sweetalert2";
import { ColumnDefintion } from "../models/ColumnDefintion";
import { OSVersionModel } from "../models/OSVersionModel";
import * as CryptoJS from 'crypto-js';
import { LogData, LogDetails, OffLineEvents } from "../models/LogDetails";
import { environment } from "src/environments/environment";
import { HelpMenuService } from "../common-components/help-menu/help-menu.service";

declare let LZString: any;
declare let $: any;
@Injectable()
export class CrewbidHelper {
  constructor(
    @Inject(forwardRef(() => GlobalStore)) public store: GlobalStore,
    @Inject(forwardRef(() => CrewbidCollection))
    public collection: CrewbidCollection,
    private helpService: HelpMenuService
  ) {}
  /**
   * @method compressToUTF16 for compressing the decompressed data
   * @param data as string
   */
  public compressToUTF16(data: any): string {
    return LZString.compressToUTF16(data);
  }

  /**
   * @method decompressFromUTF16 for decompressing the compressed data
   * @param input as string
   */
  public decompressFromUTF16(input: string, doJson: boolean): any {
    let decompressedData = LZString.decompressFromUTF16(input);
    if (doJson) {
      if (decompressedData == undefined || decompressedData == "") return;
      decompressedData = this.jsonParse(decompressedData);
    }
    return decompressedData;
  }

  /**
   * @method jsonParse for parsing to json format
   * @param data is line data
   */
  public jsonParse(data: any): any {
    const jsonLineData = JSON.parse(data);
    return values(jsonLineData);
  }

  /**
   * @method getKeyCombination for genetating the unique key combination
   * @param BidDataRequest,
   */
  public getKeyCombination(BidDataRequest: TblBidDataDisplay): string {
    try {
      return `${BidDataRequest.Month}-${BidDataRequest.Year}-${BidDataRequest.Domicile}-${BidDataRequest.Position}-${BidDataRequest.Round}`;
    } catch (error) {
      throw new Error(
        `ClassName: HelperService, FunctionName:  getKeyCombination: ${error.stack.toString()}`
      );
    }
  }

  /**
   * @method calculateBpStartDayWithYear for calculating bid period for start date
   * @param position,
   * @param month,
   * @param year,
   */
  public calculateBpStartDayWithYear(
    position: string,
    month: number,
    year: number
  ) {
    try {
      month = month - 1;
      // Javscript Month Starting From Zero
      let startDay;
      if (position === "FA") {
        if (month === 1) {
          startDay = new Date(year, month - 1, 31);
        } else if (month === 2) {
          startDay = new Date(year, month, 2);
        } else {
          startDay = new Date(year, month, 1);
        }
      } else {
        startDay = new Date(year, month, 1);
      }
      return startDay;
    } catch (error) {
      throw new Error(
        `ClassName: HelperService, FunctionName:  calculateBpStartDayWithYear: ${error.stack.toString()}`
      );
    }
  }

  /**
   * @method calculateBpStartDayWithYear for calculating bid period for end date
   * @param position,
   * @param month,
   * @param year,
   */
  public calculateBPEndDateWithYear(
    position: string,
    month: number,
    year: number
  ) {
    try {
      let endDay;
      const numberOfDays = moment(
        "" + year + "-" + month + "",
        "YYYY-MM"
      ).daysInMonth(); // DateTime.DaysInMonth(year, month);
      month = month - 1;

      if (position === "FA") {
        if (month === 1) {
          //February
          endDay = new Date(year, month + 1, 1);
        } else if (month === 0) {
          //January
          endDay = new Date(year, month, 30);
        } else {
          endDay = new Date(year, month, numberOfDays);
        }
      } else {
        if (month === 0) {
          endDay = new Date(year, month, 31);
        } else {
          endDay = new Date(year, month, numberOfDays);
        }
      }
      return endDay;
    } catch (error) {
      throw new Error(
        `ClassName: HelperService, FunctionName:  calculateBPEndDateWithYear: ${error.stack.toString()}`
      );
    }
  }

  /**
   * @method compareVersion - Compare two versions
   * @param version1 - Loacal Version
   * @param version2 - Incoming version
   */
  public compareVersion(version1, version2) {
    try {
      let flag = false;
      let compareResult;
      const maxLength = Math.max(version1.length, version2.length);
      const minLength = Math.min(version1.length, version2.length);

      for (let i = 0; i < maxLength; ++i) {
        const result = version1[i] - version2[i];
        if (result > 0) {
          flag = true;
          compareResult = 0;
          break;
        } else if (result < 0) {
          flag = true;
          compareResult = 1;
          break;
        }

        if (i === minLength) {
          if (version1.length > version1.length) {
            compareResult = version1[version1.length - 1] > 0 ? "0" : "-1";
          } else {
            compareResult = version1[version2.length - 1] > 0 ? "1" : "-1";
          }
          break;
        }
      }
      if (flag === false) {
        compareResult = -1;
      }
      return compareResult;
    } catch (error) {
      throw new Error(
        `ClassName: Helper, FunctionName:  compareVersion: ${error.stack.toString()}`
      );
    }
  }

  /**
   * @method generateCalenderData for generating calendar data
   * @param stateFile is stored state file as CrewbidState
   * @param eomDates is end of month dates as string array
   */
  public generateCalenderData(
    stateFile: CrewbidState,
    eomDates: string[]
  ): List<CalendarData> {
    try {
      const BidData: TblBidDataDisplay = this.store.getCurrentBidData();
      let vac = [];
      if (
        !BidData.isHistoricalBid &&
        stateFile &&
        stateFile.MenubarButtons &&
        (stateFile.MenubarButtons.vacationButton ||
          stateFile.MenubarButtons.swaptimizerButton) &&
        stateFile.Vacation
      ) {
        vac = stateFile.MenubarButtons.swaptimizerButton
          ? stateFile.SwaptimizerVacation
          : stateFile.Vacation;
      }

      // const BidData: TblBidDataDisplay = this.store.getCurrentBidData();
      let StartDate: Date = new Date(BidData.bidPeriodStartDate);
      const EndDate: Date = new Date(BidData.bidPeriodEndDate);

      // if (BidData.position == "FA" && (BidData.month == 2 || BidData.month == 3)) {
      //   StartDate = moment(new Date(StartDate)).subtract(1, 'day').toDate();
      // }

      const date1 = new Date(EndDate);
      const date2 = new Date(StartDate);
      const timeDiff = Math.abs(date2.getTime() - date1.getTime());
      const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));

      let iterationCount: number = this.getDateDayofWeek();
      const lstDayOfMonth: List<CalendarData> = new List<CalendarData>();
      let id = 1;
      const dayNumber = StartDate.getDay();
      if (dayNumber != 0) {
        let lastWeekDate: any = new Date(StartDate); //For testing SWA
        lastWeekDate.setDate(StartDate.getDate() - dayNumber);
        for (let count = 0; count < iterationCount; count++) {
          const CalenderDataObj = new CalendarData();
          (CalenderDataObj.id = id),
            (CalenderDataObj.isEnabled = false),
            (CalenderDataObj.DateStr = moment(new Date(lastWeekDate)).format(
              "YYYY-MM-DD"
            )),
            (CalenderDataObj.weekName = moment(new Date(lastWeekDate)).format(
              "dd"
            )),
            (CalenderDataObj.day = new Date(lastWeekDate).getDate().toString());
          if (
            vac.find(
              (x) =>
                moment(x.startDate).format() <=
                  moment(CalenderDataObj.DateStr).format() &&
                moment(x.endDate).format() >=
                  moment(CalenderDataObj.DateStr).format()
            )
          ) {
            CalenderDataObj.tripVacColor = true;
          }
          if (
            vac.find(
              (x) =>
                moment(x.startDate).format() ==
                moment(CalenderDataObj.DateStr).format()
            )
          ) {
            CalenderDataObj.vacLeftRounded = true;
          }
          if (
            vac.find(
              (x) =>
                moment(x.endDate).format() ==
                moment(CalenderDataObj.DateStr).format()
            )
          ) {
            CalenderDataObj.vacRightRounded = true;
          }
          lstDayOfMonth.Add(CalenderDataObj);
          lastWeekDate = moment(lastWeekDate).add(1, "days");
          id++;
        }
      }
      // alert(diffDays);

      const ts = diffDays; // EndDate.getDate() -StartDate.getDate();
      iterationCount = ts + 1;

      if (BidData.Position === "FA") {
        if (BidData.Month === 1) {
          iterationCount++;
        } else if (BidData.Month === 2) {
          iterationCount--;
        }
      }
      let calenderDate: any = StartDate;
      let eomDateCount = 0;
      for (let count = 1; count <= iterationCount; count++) {
        const CalenderDataObj = new CalendarData();
        CalenderDataObj.id = id;
        CalenderDataObj.isEnabled = true;
        CalenderDataObj.day = new Date(calenderDate).getDate().toString();
        CalenderDataObj.DateStr = moment(new Date(calenderDate)).format(
          "YYYY-MM-DD"
        );
        CalenderDataObj.weekName = moment(new Date(calenderDate)).format("dd");
        if (
          vac.find(
            (x) =>
              moment(x.startDate).format() <= moment(calenderDate).format() &&
              moment(x.endDate).format() >= moment(calenderDate).format()
          )
        ) {
          CalenderDataObj.tripVacColor = true;
        }
        if (
          vac.find(
            (x) => moment(x.startDate).format() == moment(calenderDate).format()
          )
        ) {
          CalenderDataObj.vacLeftRounded = true;
        }
        if (
          vac.find(
            (x) => moment(x.endDate).format() == moment(calenderDate).format()
          )
        ) {
          CalenderDataObj.vacRightRounded = true;
        }
        //For January month EOM may start at 31st for FA.
        if (eomDates && eomDates.length > 0) {
          if (eomDates.indexOf(CalenderDataObj.DateStr) == 0) {
            eomDateCount++;
            CalenderDataObj.isEOMDate = true;
            CalenderDataObj.isEOMStartDate = true;
          }
        }
        lstDayOfMonth.Add(CalenderDataObj);

        calenderDate = moment(calenderDate).add(1, "days");
        id++;
      }
      //Logic for showing the next month date atleast 3
      iterationCount = 7 - (lstDayOfMonth.Count() % 7);
      iterationCount = iterationCount > 2 ? iterationCount : iterationCount + 7;
      let datesAfterEom = false;
      // First week of next month
      for (let count = 1; count <= iterationCount; count++) {
        const CalenderDataObj = new CalendarData();
        CalenderDataObj.id = id;
        CalenderDataObj.isEnabled = false;
        CalenderDataObj.day = new Date(calenderDate).getDate().toString();
        CalenderDataObj.DateStr = moment(new Date(calenderDate)).format(
          "YYYY-MM-DD"
        );
        CalenderDataObj.weekName = moment(new Date(calenderDate)).format("dd");
        if (count > 3) {
          //Upto 3rd of next month is needed in expanded view
          CalenderDataObj.hideInExpanded = true;
        }

        if (eomDates && eomDates.length > 0) {
          if (eomDates.indexOf(CalenderDataObj.DateStr) == 0) {
            eomDateCount++;
            CalenderDataObj.isEOMDate = true;
            CalenderDataObj.isEOMStartDate = true;
          } else if (eomDates.indexOf(CalenderDataObj.DateStr) > 0) {
            eomDateCount++;
            CalenderDataObj.isEOMDate = true;
            CalenderDataObj.isEOMStartDate = false;
            datesAfterEom = true;
          } else if (datesAfterEom) {
            eomDateCount++;
            CalenderDataObj.isEOMDate = true;
            CalenderDataObj.isEOMStartDate = false;
            if (eomDateCount == 7) {
              CalenderDataObj.isEOMEndDate = true;
              datesAfterEom = false;
            }

            // if (count == iterationCount && stateFile.MenubarButtons.vacationButton) {
            //   CalenderDataObj.vacRightRounded = true;
            // }
          }
        }
        //let eomDt = eomDates?moment(new Date(eomDates[0])).format('YYYY-MM-DD'):''
        if (
          vac.find(
            (x) =>
              moment(x.startDate).format() <= moment(calenderDate).format() &&
              moment(x.endDate).format() >= moment(calenderDate).format()
          )
        ) {
          CalenderDataObj.tripVacColor = true;
        }
        if (
          vac.find(
            (x) => moment(x.startDate).format() == moment(calenderDate).format()
          )
        ) {
          CalenderDataObj.vacLeftRounded = true;
        }
        if (
          vac.find(
            (x) => moment(x.endDate).format() == moment(calenderDate).format()
          )
        ) {
          CalenderDataObj.vacRightRounded = true;
        }
        lstDayOfMonth.Add(CalenderDataObj);
        calenderDate = moment(calenderDate).add(1, "days");
        id++;
      }
      //If we have always a calender of 6 X 7 matrix uncomment this
      // iterationCount = lstDayOfMonth.Count();
      // for (let count = 0; count < 42 - iterationCount; count++) {
      //   const CalenderDataObj = new CalendarData();
      //   (CalenderDataObj.id = id),
      //     (CalenderDataObj.isEnabled = false),
      //     (CalenderDataObj.day = '');

      //   lstDayOfMonth.Add(CalenderDataObj);
      //   id++;
      // }
      return lstDayOfMonth;
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * @method getDateDayofWeek for generating days of week
   */
  public getDateDayofWeek(): number {
    try {
      const currentBidData = this.store.getCurrentBidData();
      const StartDate = currentBidData.bidPeriodStartDate;
      const DayString = moment(new Date(StartDate)).format("dddd");
      let Day;
      switch (DayString) {
        case "Sunday":
          Day = 0;
          break;
        case "Monday":
          Day = 1;
          break;
        case "Tuesday":
          Day = 2;
          break;
        case "Wednesday":
          Day = 3;
          break;
        case "Thursday":
          Day = 4;
          break;
        case "Friday":
          Day = 5;
          break;
        case "Saturday":
          Day = 6;
          break;

        default:
          break;
      }
      return Day;
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * @method setDate for making date
   * @param day is date as number
   * @param isLastTrip is last trip or not as boolean
   */
  public setDate(day: number, isLastTrip: boolean) {
    try {
      const CurrentBid = this.store.getCurrentBidData();
      let tempMonth = CurrentBid.Month;
      if (CurrentBid.Position == "FA" && CurrentBid.Month == 2) {
        if (day == 31) {
          tempMonth--;
        } else if (day == 1 && isLastTrip) {
          tempMonth++;
        }
      }
      return new Date(CurrentBid.Year, tempMonth - 1, day);
    } catch (error) {
      throw `ClassName: HelperService, FunctionName:  setDate: ${error.stack.toString()}`;
    }
  }

  /**
   * @method convertMinutesToFormattedHour for converting minutes to formated hours
   * @param minutes is minutes as number
   */
  public convertMinutesToFormattedHour(minutes: number): string {
    try {
      const hhh = Math.floor(minutes / 60)
        .toString()
        .padStart(2, "0");
      const mm = Math.ceil(minutes % 60)
        .toString()
        .padStart(2, "0");
      return hhh + ":" + mm;
    } catch (error) {
      throw `ClassName: HelperService, FunctionName: convertMinutesToFormattedHour, ErrorMessage: ${error.stack.toString()}`;
    }
  }

  /**
   * @method domicileTimeFromHerb for finding the domicile herb time
   * @param domicile is city as sting
   * @param herb is trip time as number
   */
  public domicileTimeFromHerb(domicile: string, herb: number): number {
    try {
      switch (domicile) {
        case "ATL":
          return herb + 60; // EST = herb + 60
        case "BWI":
          return herb + 60; // EST = herb + 60
        case "BNA":
          return herb; // CST = herb
        case "DAL":
          return herb; // CST = herb
        case "DEN":
          return herb - 60; // MST = herb - 60
        case "HOU":
          return herb; // CST = herb
        case "LAS":
          return herb - 120; // PST = herb - 120
        case "LAX":
          return herb - 120; // PST = herb - 120
        case "MCO":
          return herb + 60; // EST = herb + 60
        case "MDW":
          return herb; // CST = herb
        case "OAK":
          return herb - 120; // PST = herb - 120
        case "PHX":
          return herb - 60; // MST = herb - 60
        default:
          return 1440;
      }
    } catch (error) {
      throw `ClassName: HelperService, FunctionName: domicileTimeFromHerb, ErrorMessage: ${error.stack.toString()}`;
    }
  }

  /**
   * @method calcTimeMinutesToHHmm for time calculation
   * @param tm is time as string
   */
  public calcTimeMinutesToHHmm(tm: string): string {
    try {
      if (tm == "0") return "0";
      let minutes: string = null;
      let hours: string = null;
      const mins = Number(tm);
      const days: number = Math.trunc(mins % (60 * 24));
      const hrs: number = Math.trunc(days / 60);
      const mns: number = Math.trunc(days % 60);
      hours = Math.floor(hrs).toString();
      if (mns.toString().length == 1) minutes = "0" + Math.ceil(mns).toString();
      else minutes = Math.ceil(mns).toString();
      const displayTime: string = hours + minutes;
      return displayTime;
    } catch (error) {
      throw `ClassName: HelperService, FunctionName: calcTimeMinutesToHHmm, ErrorMessage: ${error.stack.toString()}`;
    }
  }

  /**
   * @method timeFromMinutes for time calculation from minitues
   * @param minutes is minutes as string
   */
  public timeFromMinutes(minutes: string): string {
    try {
      let time = "";
      const hour: number = Math.trunc(Number(minutes) / 60);
      const minute: number = Math.trunc(Number(minutes) % 60);

      time = hour.toString() + minute.toString().padStart(2, "0");
      return time;
    } catch (error) {
      throw `ClassName: HelperService, FunctionName: timeFromMinutes, ErrorMessage: ${error.stack.toString()}`;
    }
  }

  /**
   * @method getNumberRangeForItemType for getting number range
   * @param from is strat range
   * @param to is end range
   */
  public getNumberRangeForItemType(from: any, to: any): ItemType[] {
    const items: ItemType[] = [];
    for (let index = parseInt(from); index <= parseInt(to); ++index) {
      items.push(new ItemType(index, index));
    }
    return items;
  }
  /**
   * @method getDecimalRangeForItemType for getting decimal range
   * @param from is strat range
   * @param to is end range
   * @param diff is the difference between the range
   * @param numPlaces is the no of digit from decimal place
   */
  public getDecimalRangeForItemType(
    from: any,
    to: any,
    diff: number,
    numPlaces: number
  ): ItemType[] {
    const items: ItemType[] = [];
    let rangeVal = parseFloat(from);
    while (rangeVal <= parseFloat(to)) {
      rangeVal =
        Math.round(rangeVal * Math.pow(10, numPlaces)) /
        Math.pow(10, numPlaces);
      items.push(new ItemType(rangeVal.toFixed(numPlaces), rangeVal));
      rangeVal = rangeVal + diff;
    }
    return items;
  }

  /**
   *
   * @param from is starting number as integer
   * @param to is ending number as integer
   * @param diff is difference number as interger
   * @returns calculated contant as Item type
   */
  public getPercentageForItemType(
    from: number,
    to: number,
    diff: number
  ): ItemType[] {
    const items: ItemType[] = [];
    let rangeVal = from;
    while (rangeVal <= to) {
      items.push(new ItemType(rangeVal, rangeVal));
      rangeVal = rangeVal + diff;
    }
    return items;
  }
  /**
   * @method getDayOfWeekNumber -To get day of week number
   * @param dayOfWeek Day of Week
   */
  public getDayOfWeekNumber = (dayOfWeek: string): number => {
    let dayNumber;
    switch (dayOfWeek) {
      case "Su":
      case "sundays":
        dayNumber = DaysofWeek.Sundays;
        break;
      case "Mo":
      case "mondays":
        dayNumber = DaysofWeek.Mondays;
        break;
      case "Tu":
      case "tuesdays":
        dayNumber = DaysofWeek.Tuesdays;
        break;
      case "Wed":
      case "wednesdays":
        dayNumber = DaysofWeek.Wednesdays;
        break;
      case "Th":
      case "thursdays":
        dayNumber = DaysofWeek.Thursdays;
        break;
      case "Fr":
      case "fridays":
        dayNumber = DaysofWeek.Fridays;
        break;
      case "Sa":
      case "saturdays":
        dayNumber = DaysofWeek.Saturdays;
        break;
    }
    return dayNumber;
  };

  /**
   * @method padLeft for adding 0 before namber value
   * @param num is value as number
   * @param size is legth for the return value as number
   */
  public padLeft(num: number, size: number): string {
    let s = num + "";
    while (s.length < size) s = "0" + s;
    return s;
  }

  /**
   * @method GetMonthName for getting month name
   * @param monthNumber is month number as number
   */
  public GetMonthName(monthNumber: number): string {
    let month = "";
    switch (monthNumber) {
      case 1:
        month = "Jan";
        break;
      case 2:
        month = "Feb";
        break;
      case 3:
        month = "Mar";
        break;
      case 4:
        month = "Apr";
        break;
      case 5:
        month = "May";
        break;
      case 6:
        month = "Jun";
        break;
      case 7:
        month = "Jul";
        break;
      case 8:
        month = "Aug";
        break;
      case 9:
        month = "Sep";
        break;
      case 10:
        month = "Oct";
        break;
      case 11:
        month = "Nov";
        break;
      case 12:
        month = "Dec";
        break;
      default:
        month = "";
        break;
    }
    return month;
  }

  /**
   * @method getDefaultBidOptions for providing the bid options
   */
  public getDefaultBidOptions() {
    return {
      Base: [
        { domicileName: "ATL", isBaseEnabled: true },
        { domicileName: "AUS", isBaseEnabled: true },
        { domicileName: "BNA", isBaseEnabled: true },
        { domicileName: "BWI", isBaseEnabled: true },
        { domicileName: "DAL", isBaseEnabled: true },
        { domicileName: "DEN", isBaseEnabled: true },
        // { domicileName: "FLL", isBaseEnabled: true },
        { domicileName: "HOU", isBaseEnabled: true },
        { domicileName: "LAS", isBaseEnabled: true },
        { domicileName: "LAX", isBaseEnabled: true },
        { domicileName: "MCO", isBaseEnabled: true },
        { domicileName: "MDW", isBaseEnabled: true },
        { domicileName: "OAK", isBaseEnabled: true },
        { domicileName: "PHX", isBaseEnabled: true },
      ],
      Positions: [{ btnname: "CP" }, { btnname: "FO" }, { btnname: "FA" }],

      Rounds: [
        { btnname: "1st Round", id: 1 },
        { btnname: "2nd Round", id: 2 },
      ],
      Months: [
        { btnname: "Jan", id: 1, isMonthEnabled: true },
        { btnname: "Feb", id: 2, isMonthEnabled: true },
        { btnname: "Mar", id: 3, isMonthEnabled: true },
        { btnname: "Apr", id: 4, isMonthEnabled: true },
        { btnname: "May", id: 5, isMonthEnabled: true },
        { btnname: "Jun", id: 6, isMonthEnabled: true },
        { btnname: "Jul", id: 7, isMonthEnabled: true },
        { btnname: "Aug", id: 8, isMonthEnabled: true },
        { btnname: "Sep", id: 9, isMonthEnabled: true },
        { btnname: "Oct", id: 10, isMonthEnabled: true },
        { btnname: "Nov", id: 11, isMonthEnabled: true },
        { btnname: "Dec", id: 12, isMonthEnabled: true },
      ],

      Year: [],
    };
  }

  /**
   * @method getMonthNumber for getting month number
   * @param monthName is month name as string
   */
  public getMonthNumber(monthName: string): number {
    let month = -1; // -1 For default
    switch (monthName) {
      case "Jan":
        month = 1;
        break;
      case "Feb":
        month = 2;
        break;
      case "Mar":
        month = 3;
        break;
      case "Apr":
        month = 4;
        break;
      case "May":
        month = 5;
        break;
      case "Jun":
        month = 6;
        break;
      case "Jul":
        month = 7;
        break;
      case "Aug":
        month = 8;
        break;
      case "Sep":
        month = 9;
        break;
      case "Oct":
        month = 10;
        break;
      case "Nov":
        month = 11;
        break;
      case "Dec":
        month = 12;
        break;
      default:
        month = -1;
        break;
    }
    return month;
  }

  public positionAbbrevation(shortName: string): string {
    let abbrName: string;
    switch (shortName) {
      case "CP":
        abbrName = "Captain";
        break;
      case "FA":
        abbrName = "Flight Attendant";
        break;
      case "FO":
        abbrName = "First Officer";
    }
    return abbrName;
  }

  /**
   * @method getEOMDatesArray To get EOM applied Dates from Line Data
   * @param lines as Lines
   */
  public getEOMDatesArray(lines: Lines[]) {
    return lines
      .find((x) => x.BidLineTemplates)
      .BidLineTemplates.filter(
        (x) =>
          (x.BidLineType == BidLineType.VA ||
            x.BidLineType == BidLineType.VAP) &&
          !x.IsInCurrentMonth
      )
      .map((x) => moment(new Date(x.Date)).format("YYYY-MM-DD"));
  }
  /**
   * @method numberOnly To make text box value as numeric only
   * @param event key press event data
   */
  public numberOnly(event): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  /**
   * @method generateCrewbidbidTxtFileNames for creating the selected file name
   * @param bidData is current bid details as TblBidDataDisplay
   * @param file is selected file as string
   */
  public generateCrewbidbidTxtFileNames(
    bidData: TblBidDataDisplay,
    file: string,
    enableSwaApi? :boolean
  ): string {
    try {
      let fileName: string;
      if (file == "Seniority List") {
        fileName =
          bidData.Position == "FA"
            ? bidData.Round == 1
              ? "S"
              : "SR"
            : bidData.Round == 1
            ? "S"
            : "R";
      } else if (file == "Cover Letter" && !enableSwaApi) {
        fileName =
          bidData.Position == "FA"
            ? bidData.Round == 1
              ? "C"
              : "CR"
            : bidData.Round == 1
            ? "C"
            : "R";
      } else if (file == "Cover Letter" && enableSwaApi) {
        if (bidData.Position == "FA") {
          const { Domicile, Year, Month, Round } = bidData;
          const bidDetails = `${Domicile}${Year}${Month.toString().padStart(2, '0')}${Round}`;
          return `CL_${bidDetails}.pdf`;
        } else {
          fileName = bidData.Round == 1? "C": "R";
        }
      } else if (file == "Line Text") {
        fileName = bidData.Round == 1 ? "L" : "N";
      } else if (file == "Trip Text") {
        fileName =
          bidData.Position == "FA" ? (bidData.Round == 2 ? "T" : "P") : "P";
      } else if (file == "FA Memo") {
        fileName = bidData.Round == 1 ? "O" : "OR";
      } else {
        fileName = bidData.Round == 1 ? "M" : "W";
      }
      return bidData.Domicile + bidData.Position + fileName + ".TXT";
    } catch (error) {
      throw `ClassName: HelperService, FunctionName:  generateCrewbidbidTxtFileNames: ${error.stack.toString()}`;
    }
  }
  /**
   * @method reOrderArray Re order an array with another array
   * @param array array to re order
   * @param order re order pattern as integer array
   * @param key Key to reorder
   * @returns re ordered array
   */
  public reOrderArray(array: any, order: number[], key: string) {
    array.sort(function (a, b) {
      const A = a[key],
        B = b[key];
      if (order.indexOf(A) > order.indexOf(B)) {
        return 1;
      } else {
        return -1;
      }
    });
    return array;
  }
  /**
   * @method createCalenderDate Create Calender date
   */
  public createCalenderDate(): CalendarView[] {
    try {
      const BidData: TblBidDataDisplay = this.store.getCurrentBidData();
      const StartDate: Date = new Date(BidData.bidPeriodStartDate);
      const EndDate: Date = new Date(BidData.bidPeriodEndDate);
      const date1 = new Date(EndDate);
      const date2 = new Date(StartDate);
      const timeDiff = Math.abs(date2.getTime() - date1.getTime());
      const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
      const ts = diffDays; // EndDate.getDate() -StartDate.getDate();
      let iterationCount: number = this.getDateDayofWeek();
      const lstDayOfMonth: CalendarView[] = [];
      let id = 0;
      const dayNumber = StartDate.getDay();
      if (dayNumber != 0) {
        let lastWeekDate: any = new Date(BidData.bidPeriodStartDate);
        lastWeekDate.setDate(StartDate.getDate() - dayNumber);
        //Last week of last month
        for (let count = 0; count < iterationCount; count++) {
          const CalenderDataObj = new CalendarView();
          CalenderDataObj.dateIndex = id;
          CalenderDataObj.isEnabled = false;
          CalenderDataObj.day = new Date(lastWeekDate).getDate().toString();
          CalenderDataObj.dateString = moment(new Date(lastWeekDate)).format(
            "YYYY-MM-DD"
          );

          lstDayOfMonth.push(CalenderDataObj);
          lastWeekDate = moment(lastWeekDate).add(1, "days");
          id++;
        }
      }

      iterationCount = ts + 1;

      if (BidData.Position === "FA") {
        if (BidData.Month === 1) {
          iterationCount++;
        } else if (BidData.Month === 2) {
          iterationCount--;
        }
      }

      let calenderDate: any = StartDate;
      for (let count = 1; count <= iterationCount; count++) {
        const CalenderDataObj = new CalendarView();
        CalenderDataObj.dateIndex = id;
        CalenderDataObj.isEnabled = true;
        CalenderDataObj.day = new Date(calenderDate).getDate().toString();
        CalenderDataObj.dateString = moment(new Date(calenderDate)).format(
          "YYYY-MM-DD"
        );
        lstDayOfMonth.push(CalenderDataObj);
        calenderDate = moment(calenderDate).add(1, "days");
        id++;
      }
      //Logic for showing the next month date atleast 3
      iterationCount = 7 - (lstDayOfMonth.length % 7);
      iterationCount = iterationCount > 2 ? iterationCount : iterationCount + 7;
      //if (EndDate.getDay() != 6) {
      // First week of next month
      for (let count = 1; count <= iterationCount; count++) {
        const CalenderDataObj = new CalendarView();
        CalenderDataObj.dateIndex = id;
        CalenderDataObj.isEnabled = false;
        CalenderDataObj.day = new Date(calenderDate).getDate().toString();
        CalenderDataObj.dateString = moment(new Date(calenderDate)).format(
          "YYYY-MM-DD"
        );
        lstDayOfMonth.push(CalenderDataObj);
        calenderDate = moment(calenderDate).add(1, "days");
        id++;
      }
      //}
      return lstDayOfMonth;
    } catch (error) {
      throw `ClassName: HelperService, FunctionName:  createCalenderDate: ${error.stack.toString()}`;
    }
  }

  /**
   * @method getTimeZoneFromUTC for getting the time zone from UTC
   * @param fltTime is flight time as string
   * @param station is employee station as string
   * @param userCities is employee cities as City[]
   * @returns converted time on the particular area;
   */
  public getTimeZoneFromUTC(
    fltTime: string,
    station: string,
    userCities: City[]
  ): string {
    try {
      const time = this.calcTimeMinutesToHHmm(fltTime.toString());
      const inputTime =
        moment(new Date()).format("YYYY-MM-DD") +
        " " +
        [
          time.substr(0, time.length - 2),
          ":",
          time.substr(time.length - 2, time.length),
        ].join("");
      const fmt = "YYYY-MM-DDTHH:mm:ssZ";
      const zone = "US/Central";
      const utcdepTime = momenttime.tz(inputTime, fmt, zone).utc().format(fmt);
      const momentdepTime = moment(utcdepTime);
      return momentdepTime
        .tz(userCities.find((x) => x.Name == station).ZoneArea)
        .format("HHmm")
        .toString();
    } catch (error) {
      throw `ClassName: HelperService, FunctionName:  getTimeZoneFromUTC: ${error.stack.toString()}`;
    }
  }

  /**
   * @method getFirstSunday for getting the first sunday
   * @param year is current year as integer
   * @param month is current month as integer
   * @returns first sunday as string
   */
  public getFirstSunday(year: number, month: number): string {
    for (let i = 1; i <= 3; i++) {
      const date = month.toString() + "/" + i.toString() + "/" + year.toString();
      if (moment(date, "MM/DD/YYYY").format("dddd") == "Sunday") {
        return moment(date, "MM/DD/YYYY").format("MM/DD/YYYY");
      }
    }
  }

  /**
   * @method getWeeksStartAndEndInMonth for getting the each weeks and its corresponing codes
   * @param _month is current month as integer
   * @param _year is current year as integer
   * @param returnDate
   * @returns weeks with code as array
   */
  public getWeeksStartAndEndInMonth(
    _month: number,
    _year: number,
    returnDate?
  ) {
    const monthYear = _month + "/" + _year;
    const lasDay = moment(monthYear, "MM/YYYY")
      .endOf("month")
      .format("MM/DD/YYYY");
    const getLastDate = parseInt(moment(lasDay).format("DD"));
    const weeks = [];
    let _days = [];
    const codes = ["A", "B", "C", "D", "E", "F"];
    for (let day = 1; day <= getLastDate; day++) {
      const thisDay = moment(_month + "/" + day + "/" + _year, "MM/DD/YYYY");
      const getthisDay = thisDay.weekday();
      const getthidDate = parseInt(moment(thisDay).format("DD"));
      _days.push(
        returnDate
          ? thisDay
          : moment(_month + "/" + day + "/" + _year, "MM/DD/YYYY").format(
              "MM/DD/YYYY"
            )
      );
      if (getthisDay === 6) {
        weeks.push({ start: _days[0], end: _days[_days.length - 1], code: "" });
        _days = [];
      } else if (getthidDate === getLastDate) {
        weeks.push({ start: _days[0], end: _days[_days.length - 1], code: "" });
      }
    }
    weeks[0].start = moment(weeks[0].end, "MM/DD/YYYY")
      .subtract(6, "days")
      .format("MM/DD/YYYY");
    weeks[weeks.length - 1].end = moment(
      weeks[weeks.length - 1].start,
      "MM/DD/YYYY"
    )
      .add(6, "days")
      .format("MM/DD/YYYY");
    for (let i = 0; i < weeks.length; i++) {
      weeks[i].code = codes[i];
    }
    return weeks;
  }

  /**
   * @method getDeadheadCities for getting the deadhead cities
   * @param trips is current trip details
   * @param type is deadhead type (1 - DH start, 2 - DH end, 3 - DH either)
   * @returns all deadhead cityes corresponding to its type
   */
  public getDeadheadCities(trips: Trip[], type: number): string[] {
    let cities: string[] = [];
    const lineTripArray: string[] = JSON.parse(this.encryptDecrypt(localStorage.getItem("lineTripArray"), false));
    trips.forEach((tr) => {
      if (lineTripArray.includes(tr.TripNum)) {
        tr.DutyPeriods.forEach((dp, i) => {
          if (type == 1 && i == 0 && dp.Flights[0].DeadHead) {
            cities.push(dp.Flights[0].ArrSta);
          } else if (
            type == 2 &&
            i == tr.DutyPeriods.length - 1 &&
            dp.Flights[dp.Flights.length - 1].DeadHead
          ) {
            cities.push(dp.Flights[dp.Flights.length - 1].DepSta);
          } else if (type == 3) {
            if (i == 0 && dp.Flights[0].DeadHead) {
              cities.push(dp.Flights[0].ArrSta);
            } else if (
              i == tr.DutyPeriods.length - 1 &&
              dp.Flights[dp.Flights.length - 1].DeadHead
            ) {
              cities.push(dp.Flights[dp.Flights.length - 1].DepSta);
            }
          }
        });
      }
    });
    cities = uniq(cities);
    return orderBy(cities);
  }
  /**
   * @method inputTimeChange Input time change event
   * @param event event with input time a string
   */
  public inputTimeChange(event) {
    try {
      let isValid = false;
      let inputTime = event.target.value;
      if (inputTime.length > 0 && inputTime.length <= 4) {
        inputTime = inputTime.toString().padStart(4, "0");
        if (moment(inputTime, "HHmm", true).isValid()) isValid = true;
      }
      if (!isValid && inputTime.length != 0) {
        Swal.fire({
          title: "Invalid Input",
          text: "Please input 24 or less for the hours and 59 or less for the minutes.",
          confirmButtonText: "Ok",
          backdrop: false,
        });
        event.target.value = "";
        return false;
      } else event.target.value = inputTime;
      return true;
    } catch (error) {
      throw `ClassName: HelperService, FunctionName:  inputTimeChange: ${error.stack.toString()}`;
    }
  }

  /**
  public genarateConnectTime = (): ItemType[] => {
   * @method genarateConnectTime for generating the connect times
   * @returns generated connect times
   */
  public genarateConnectTime = (): ItemType[] => {
    try {
      const valueToReturn: ItemType[] = [];
      for (let i = 5; i < 60; i = i + 5) {
        valueToReturn.push(
          new ItemType(
            `00:${i.toString().padStart(2, "0")}`,
            `00:${i.toString().padStart(2, "0")}`
          )
        );
      }
      valueToReturn.push(new ItemType("01:00", "01:00"));
      return valueToReturn;
    } catch (error) {
      throw `ClassName: HelperService, FunctionName:  genarateConnectTime: ${error.stack.toString()}`;
    }
  };

  /**
   * @method genarateCheckInTime for generating checkin time
   * @returns generated checkin time
   */
  public genarateCheckInTime = (): ItemType[] => {
    try {
      const valueToReturn: ItemType[] = [];
      for (let i = 5; i <= 180; i = i + 5) {
        const temp = this.convertMinutesToFormattedHour(i);
        valueToReturn.push(new ItemType(temp, temp));
      }
      return valueToReturn;
    } catch (error) {
      throw `ClassName: HelperService, FunctionName:  genarateCheckInTime: ${error.stack.toString()}`;
    }
    //ListCheckInTime.Add("01:00");
  };

  /**
   * @method genarateBackToBaseTime for generating back to base time
   * @returns generated back to base time
   */
  public genarateBackToBaseTime = (): ItemType[] => {
    try {
      const valueToReturn: ItemType[] = [];
      for (let i = 5; i < 60; i = i + 5) {
        valueToReturn.push(
          new ItemType(
            `00:${i.toString().padStart(2, "0")}`,
            `00:${i.toString().padStart(2, "0")}`
          )
        );
      }
      valueToReturn.push(new ItemType("01:00", "01:00"));
      return valueToReturn;
    } catch (error) {
      throw `ClassName: HelperService, FunctionName:  genarateBackToBaseTime: ${error.stack.toString()}`;
    }
  };

  /**
   * @method setDate for making date
   * @param day is date as number
   * @param currentBid is the bid data object
   * @param isLastTrip is last trip or not as boolean
   */
  public getStartDate(
    day: number,
    currentBid: TblBidDataDisplay,
    isLastTrip: boolean
  ) {
    try {
      let tempMonth = currentBid.Month;
      if (currentBid.Position == "FA" && currentBid.Month == 2) {
        if (day == 31) {
          tempMonth--;
        } else if (day == 1 && isLastTrip) {
          tempMonth++;
        }
      }
      return new Date(currentBid.Year, tempMonth - 1, day);
    } catch (error) {
      throw `ClassName: HelperService, FunctionName:  setDate: ${error.stack.toString()}`;
    }
  }
  /**
   * @method setColDefDataFormat Set column definition data format
   * @param colDef Column definition item
   * @param value Value of the property
   * @returns value with suitable format
   */
  public setColDefDataFormat(colDef: ColumnDefintion, value: any): any {
    try {
      if (
        value === undefined ||
        value === null ||
        value === "" ||
        (typeof value == "object" && value.length == 0)
      ) {
        value = "0";
      }
      switch (colDef.DataFormat) {
        case ColDefDataFormats.Number:
          value = value;
          break;
        case ColDefDataFormats.FloatSingleDec:
          if (typeof value == "string") value = parseFloat(value);
          value = value.toFixed(1);
          break;
        case ColDefDataFormats.FloatDoubleDec:
          if (typeof value == "string") value = parseFloat(value);
          value = value.toFixed(2);
          break;
        case ColDefDataFormats.FloatDoubleDecWithColor:
          if (typeof value == "string") value = parseFloat(value);
          value = value.toFixed(2);
          break;
        case ColDefDataFormats.FloatTripleDec:
          if (typeof value == "string") value = parseFloat(value);
          value = value.toFixed(3);
          break;
        case ColDefDataFormats.TimeTotHours:
          if (value == "0") {
            value = "00:00";
          }
          break;
        case ColDefDataFormats.TimeHHmm:
          if (value.includes(".")) {
            value = value.split(".")[1];
          }
          if (value != "24:00")
            value = moment(value, "HH:mm:ss").format("H:mm");
          break;
        case ColDefDataFormats.TimeHHmmss:
          if (value != "24:00:00")
            value = moment(value, "HH:mm:ss").format("H:mm:ss");
          break;
        case ColDefDataFormats.FourDigit:
          value = value.toString().padStart(4, "0");
          break;
        case ColDefDataFormats.Percentage:
          if (value !== "0" && value !== 0) {
            value = value.toFixed(2) + "%";
          }
          break;
        default:
      }
      return value;
    } catch (error) {
      throw `ClassName: HelperService, FunctionName:  setColDefDataFormat: ${error.stack.toString()}`;
    }
  }

  /**
   * @method checkSameDayInMinutes for checking the time is next day is or not
   * @param startTime is staring time
   * @param endTime is ending time
   * @returns true if same date time else false
   */
  public checkSameDayInMinutes(startTime: number, endTime: number): boolean {
    return Math.floor(startTime / 1440) == Math.floor(endTime / 1440);
  }

  /**
   * @method detectPlatForm for identifing the user's device and browser
   * @returns device details such as OS, browser and browser version
   */
  public detectPlatForm = (): OSVersionModel => {
    const OSData = new OSVersionModel();
    OSData.OS = navigator.userAgent.toLowerCase();
    if (OSData.OS.includes("edge")) {
      OSData.BrowserName = "Microsoft Edge";
      OSData.Version = OSData.OS.substring(OSData.OS.indexOf("edge") + 5);
    } else if (OSData.OS.includes("chrome")) {
      OSData.BrowserName = "Google Chrome";
      OSData.Version = OSData.OS.substring(
        OSData.OS.indexOf("chrome/") + 7
      ).split(" ")[0];
    } else if (OSData.OS.includes("version")) {
      OSData.BrowserName = "MAC Version Safari";
      OSData.Version = OSData.OS.substring(
        OSData.OS.indexOf("version/") + 8
      ).split(" ")[0];
    } else if (OSData.OS.includes("firefox")) {
      OSData.BrowserName = "Firefox";
      OSData.Version = OSData.OS.substring(OSData.OS.indexOf("firefox/") + 8);
    } else {
      OSData.BrowserName = "Internet Explorer";
      OSData.Version = OSData.OS.substring(OSData.OS.indexOf("rv") + 3).split(
        ")"
      )[0];
    }
    if (OSData.OS.includes("windows")) OSData.OSName = "Windows";
    else if (OSData.OS.includes("macintosh")) OSData.OSName = "Macintosh";
    return OSData;
  };

  /**
   * @method getUserId To get current user id
   * @param noNeedSecretUser boolean for exclude secret user
   * @returns employee id as string
   */
  public getUserId(noNeedSecretUser?: boolean): string {
    const secretUser = noNeedSecretUser
      ? undefined
      : localStorage.getItem("userSec");
    return secretUser == undefined || secretUser == null
      ? localStorage.getItem("empnum").replace(/[^\d.]/g, "")
      : secretUser.replace(/[^\d.]/g, "");
  }

    /**
   * @method genarateGroundTime for generating ground time for filters
   * @returns generated ground time
   */
    public genarateGroundTime = (from: any, to: any, range: any): ItemType[] => {
      try {
        const valueToReturn: ItemType[] = [];
        for (let i = parseInt(from); i <= parseInt(to); i = i + parseInt(range)) {
          const temp = this.convertMinutesToFormattedHour(i);
          valueToReturn.push(new ItemType(temp, temp));
        }
        return valueToReturn;
      } catch (error) {
        throw `ClassName: HelperService, FunctionName:  genarateGroundTime: ${error.stack.toString()}`;
      }
    };

      /**
   * @method convertTimeToMinutes for converting time to minutes (hh:mm)
   * @param time is time in (hh:mm) format
   */
  public convertTimeToMinutes(time: string): number {
    try {
      const arrTime = time.split(':');
      const minutes = (parseInt(arrTime[0]) * 60) + parseInt(arrTime[1]);
      return minutes;
    } catch (error) {
      throw `ClassName: HelperService, FunctionName: convertTimeToMinutes, ErrorMessage: ${error.stack.toString()}`;
    }
  }

  /**
   * @method encryptDecrypt to encrypt and store data in localstorage
   * @param data is value to be encrypted
   */

  public encryptDecrypt(data: string, isEncrypt: boolean): string {
    try {
      let key = '0' //key should be same for both encrypt and decrypt.
      if (isEncrypt) {
        let encryptdata = CryptoJS.AES.encrypt(
          String(data),
          key
        ).toString();
        return encryptdata;
      } else {
        let decryptData = CryptoJS.AES.decrypt(
          String(data),
          key
        ).toString(CryptoJS.enc.Utf8);
        return decryptData;
      }
    } catch (error) {
      throw `ClassName: HelperService, FunctionName: encryptDecrypt, ErrorMessage: ${error.stack.toString()}`;
    }

  }

  /**
   * @method generateFVDates to generate FV vacation dates
   * @param FVDates is FV vacation dates in array.
   */
  public generateFVDates(FVDates: any): string[] {
    try {
      let dates: string[] = [];
      for (let index = 0; index < FVDates.length; index++) {
        for (let i = 0; i < 10; i++) {
          let addDate = moment(FVDates[index].FVStartDate).add(i, 'day');
          if (addDate <= moment(FVDates[index].FVEndDate)) {
            dates.push(addDate.format('YYYY/MM/DD'));
          } else {
            break;
          }
        }
      }
      return dates;
    } catch (error) {
      throw `ClassName: HelperService, FunctionName: generateFVDates, ErrorMessage: ${error.stack.toString()}`;
    }

  }
  /**
 * @method logEventDetails To add log event details. 
 */
  public logEventDetails(currentBid: TblBidDataDisplay, eventName: string, logMsg: string) {
    try {
      let logDetail: LogDetails = {
        employeeNumber: Number(currentBid.EmpNum),
        event: eventName,
        base: currentBid.Domicile,
        month: currentBid.Month.toString(),
        position: currentBid.Position,
        round: currentBid.Round,
        message: logMsg,
        operatingSystemNum: AppConstants.Platform.toString(),
        versionNumber: environment.version,
        platformNumber: AppConstants.Platform.toString(),
        bidForEmpNum: Number(currentBid.EmpNum),
        ipAddress: '',
        fromAppNum: Number(AppConstants.AppNum),
        date: moment().format('YYYY-MM-DD HH:mm:ss')
      }
      this.addLogEventDetails(logDetail);
    } catch (error) {
      throw `ClassName: HelperService, Function Name:logEventDetails  ${error.stack.toString()}`
    }
  }

  /**
 * @method addLogEventDetails To add log event details to the service. 
 */
  public addLogEventDetails(logDetail: LogDetails) {
    try {
      let logData: LogData = {
        logDetails: logDetail,
        date: moment().format('YYYY-MM-DD HH:mm:ss')
      }
      let offlineEvents: OffLineEvents = {
        eventLogs: [logData]
      }
      this.helpService.LogOffLineEvents(offlineEvents).subscribe(res => {
        if (res) { }
      });
    } catch (error) {
      throw `ClassName: HelperService, Function Name:addLogEventDetails  ${error.stack.toString()}`
    }
  }

    /**
   * @method getDateZoneFromUTC for getting the datetime zone from UTC
   * @param inputDateTime datetime for convert to UTC
   * @param station is employee station as string
   * @param userCities is employee cities as City[]
   * @returns converted time on the particular area;
   */
    public getDateZoneFromUTC(
      inputDateTime: Date,
      station: string,
      userCities: City[]
    ): string {
      try {
        const targetTimeZone = userCities.find((x) => x.Name == station).ZoneArea;
        const zone = "US/Central";
        const format = "ddd MMM DD YYYY HH:mm:ss";
        const formattedDateTime = moment(inputDateTime).format(format);
        const targetDateTime = moment.tz(formattedDateTime, format, zone).tz(targetTimeZone);
        return targetDateTime.format(format);
      } catch (error) {
        throw `ClassName: HelperService, FunctionName:  getDateZoneFromUTC: ${error.stack.toString()}`;
      }
    }

  generateRandomString(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return result;
  }
  
  generateCodeVerifier(): string {
    return this.generateRandomString(128);
  }

  generateCodeChallenge(codeVerifier: string): string {
    const codeChallenge = CryptoJS.SHA256(codeVerifier).toString(CryptoJS.enc.Base64)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
    return codeChallenge;
  }

  /**
   * Helper to decode the payload of a JWT token.
   */
  private decodeJWTPayload(token: string): any {
    const tokenParts = token.split('.'); // JWT has 3 parts: header.payload.signature
    if (tokenParts.length !== 3) {
      throw new Error('Invalid token format');
    }
    const base64Payload = tokenParts[1];
    const jsonPayload = atob(base64Payload);
    return JSON.parse(jsonPayload);
  }

  /**
   * @method decodeEmpNumFromSWAToken - Get employee number from the PKCE token
   */
  public decodeEmpNumFromSWAToken(): string | null {
    try {
      const token = localStorage.getItem('PKCEToken');
      if (!token) {
        throw new Error('PKCE token not found in localStorage');
      }
      const decodedPayload = this.decodeJWTPayload(token);
      const empNum = decodedPayload?.cn || decodedPayload?.sub;
      return empNum ? empNum.slice(1) : null;
    } catch (error) {
      console.error('Error decoding employee number from token:', error);
      return null;
    }
  }

  /**
   * @method decodeUserTypeFromSWAToken - Check if user is an FA user (Attendant group)
   */
  public decodeUserTypeFromSWAToken(token: string): boolean {
    try {
      const decodedPayload = this.decodeJWTPayload(token);
      return decodedPayload.groups?.includes('Attendant') ?? false;
    } catch (error) {
      console.error('Error decoding user type from token:', error);
      return false;
    }
  }

  /**
   * @method allowOnlyNumbers allow to enter only number in text field. 
  */
  public allowOnlyNumbers(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        event.preventDefault(); // Block non-numeric input
    }
  }
}
